import React, { useEffect, useState, memo, useRef } from "react";
import toast from "react-hot-toast";
import {
  branchService,
  categoryService,
  departmentService,
  designationService,
  gradesService,
  reasonsService,
  subDepartmentService,
  employeeService,
} from "../../../services/api";
import CompanyModal from "./CompanyTable";
import Enterprise from "../Enterprise";
import {
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Plus,
  Search,
  Settings,
} from "lucide-react";
import Modal from "./Modal";
import Filters from "../Filters";

const initialCompanies = [
  {
    id: 1,
    code: "C001",
    name: "ABC Pvt Ltd",
    email: "abc@example.com",
    password: "password123",
    phone: "1234567890",
  },
  {
    id: 2,
    code: "C002",
    name: "XYZ Corp",
    email: "xyz@example.com",
    password: "password456",
    phone: "9876543210",
  },
  ...Array.from({ length: 18 }, (_, i) => ({
    id: i + 3,
    code: `C00${i + 3}`,
    name: `Company ${i + 3}`,
    email: `company${i + 3}@example.com`,
    password: `password${i + 3}`,
    phone: `123456789${i}`,
  })),
];

export function ReportsScreen() {
  const [showFilters, setShowFilters] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [darkMode, setDarkMode] = useState(false);

  // Sample employee data for testing
  const sampleEmployees = [
    {
      id: 1,
      empCode: "EMP001",
      email: "john.doe@company.com",
      department: { name: "Engineering" },
      designation: { name: "Software Engineer" },
      branch: { name: "Head Office" },
      employmentType: "full-time",
      employmentStatus: "confirmed",
      ctc: 800000,
      user: {
        name: "John Doe",
        phone: "9876543210",
        dateOfBirth: "1990-01-15",
        gender: "Male",
      },
    },
    {
      id: 2,
      empCode: "EMP002",
      email: "jane.smith@company.com",
      department: { name: "HR" },
      designation: { name: "HR Manager" },
      branch: { name: "Branch Office" },
      employmentType: "full-time",
      employmentStatus: "probation",
      ctc: 600000,
      user: {
        name: "Jane Smith",
        phone: "9876543211",
        dateOfBirth: "1992-05-20",
        gender: "Female",
      },
    },
    {
      id: 3,
      empCode: "EMP003",
      email: "bob.wilson@company.com",
      department: { name: "Sales" },
      designation: { name: "Sales Executive" },
      branch: { name: "Head Office" },
      employmentType: "contract",
      employmentStatus: "confirmed",
      ctc: 500000,
      user: {
        name: "Bob Wilson",
        phone: "9876543212",
        dateOfBirth: "1988-11-30",
        gender: "Male",
      },
    },
  ];

  const handleApplyFilters = (filteredEmployees, currentFilters) => {
    console.log("Applied filters:", currentFilters);
    console.log("Filtered employees:", filteredEmployees);

    // Use sample data if no employees returned from API
    if (filteredEmployees && filteredEmployees.length > 0) {
      setEmployees(filteredEmployees);
    } else {
      // Apply basic filtering on sample data for demo
      const filtered = sampleEmployees.filter((emp) => {
        if (currentFilters.branch && emp.branch?.name !== currentFilters.branch)
          return false;
        if (
          currentFilters.department &&
          emp.department?.name !== currentFilters.department
        )
          return false;
        if (
          currentFilters.employmentType &&
          emp.employmentType !== currentFilters.employmentType
        )
          return false;
        if (
          currentFilters.employmentStatus &&
          emp.employmentStatus !== currentFilters.employmentStatus
        )
          return false;
        return true;
      });
      setEmployees(filtered);
    }

    setShowFilters(false);
  };

  return (
    <div
      className={`min-h-screen ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      {/* Header */}
      <div
        className={`p-6 border-b ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        }`}
      >
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Employee Reports</h1>
          <div className="flex space-x-4">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`px-4 py-2 rounded-lg ${
                darkMode
                  ? "bg-gray-700 hover:bg-gray-600"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {darkMode ? "Light Mode" : "Dark Mode"}
            </button>
            <button
              onClick={() => setShowFilters(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Open Filters
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            className={`p-4 rounded-lg ${
              darkMode ? "bg-gray-800" : "bg-white border"
            }`}
          >
            <h3 className="text-lg font-semibold">Total Employees</h3>
            <p className="text-2xl font-bold text-blue-600">
              {employees.length}
            </p>
          </div>
          <div
            className={`p-4 rounded-lg ${
              darkMode ? "bg-gray-800" : "bg-white border"
            }`}
          >
            <h3 className="text-lg font-semibold">Sample Data</h3>
            <p className="text-2xl font-bold text-green-600">
              {sampleEmployees.length}
            </p>
          </div>
        </div>

        {/* Employees Table */}
        <div
          className={`rounded-lg overflow-hidden ${
            darkMode ? "bg-gray-800" : "bg-white border"
          }`}
        >
          <table className="w-full">
            <thead className={darkMode ? "bg-gray-700" : "bg-gray-100"}>
              <tr>
                <th className="p-3 text-left">Employee ID</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Department</th>
                <th className="p-3 text-left">Designation</th>
                <th className="p-3 text-left">Branch</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">CTC</th>
              </tr>
            </thead>
            <tbody>
              {employees.length > 0 ? (
                employees.map((employee) => (
                  <tr
                    key={employee.id}
                    className={`border-t ${
                      darkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <td className="p-3">{employee.empCode}</td>
                    <td className="p-3">{employee.user?.name}</td>
                    <td className="p-3">{employee.department?.name}</td>
                    <td className="p-3">{employee.designation?.name}</td>
                    <td className="p-3">{employee.branch?.name}</td>
                    <td className="p-3 capitalize">
                      {employee.employmentType}
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          employee.employmentStatus === "confirmed"
                            ? "bg-green-100 text-green-800"
                            : employee.employmentStatus === "probation"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {employee.employmentStatus}
                      </span>
                    </td>
                    <td className="p-3">₹{employee.ctc?.toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-gray-500">
                    No employees found. Try applying different filters or check
                    the sample data.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sample Data Section for Testing */}
        <div className="mt-8">
          <h2
            className={`text-xl font-bold mb-4 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Sample Data (For Testing)
          </h2>
          <div
            className={`rounded-lg overflow-hidden ${
              darkMode ? "bg-gray-800" : "bg-white border"
            }`}
          >
            <table className="w-full">
              <thead className={darkMode ? "bg-gray-700" : "bg-gray-100"}>
                <tr>
                  <th className="p-3 text-left">Employee ID</th>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Department</th>
                  <th className="p-3 text-left">Branch</th>
                </tr>
              </thead>
              <tbody>
                {sampleEmployees.map((employee) => (
                  <tr
                    key={employee.id}
                    className={`border-t ${
                      darkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <td className="p-3">{employee.empCode}</td>
                    <td className="p-3">{employee.user?.name}</td>
                    <td className="p-3">{employee.department?.name}</td>
                    <td className="p-3">{employee.branch?.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Filters Modal */}
      {showFilters && (
        <Filters
          onApply={handleApplyFilters}
          onClose={() => setShowFilters(false)}
          darkMode={darkMode}
          initialFilters={{}}
        />
      )}
    </div>
  );
}

const Masters = memo(
  function Masters({ darkMode, setCurrentPage, toggleDarkMode }) {
    const [activeTab, setActiveTab] = useState("enterprise");
    const [companies, setCompanies] = useState(initialCompanies);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [subDepartments, setSubDepartments] = useState([]);
    const [grades, setGrades] = useState([]);
    const [categories, setCategories] = useState([]);
    const [resignationReasons, setResignationReasons] = useState([]);
    const [terminationReasons, setTerminationReasons] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [isCreateCompanyOpen, setIsCreateCompanyOpen] = useState(false);
    const [isEditCompanyOpen, setIsEditCompanyOpen] = useState(false);
    const [searchTerms, setSearchTerms] = useState({
      branch: "",
      department: "",
      designation: "",
      subDepartment: "",
      grade: "",
      category: "",
      reasons: "",
    });
    const [formData, setFormData] = useState({
      code: "",
      name: "",
      address: "",
      email: "",
      phone: "",
      password: "",
      pfCode: "",
      esiCode: "",
      labourLicense: "",
      domainName: "",
      contactPerson: "",
      website: "",
      superAdminID: "",
      logo: null,
    });
    const [inviteAdmin, setInviteAdmin] = useState(false);

    const [confirmState, setConfirmState] = useState({
      isOpen: false,
      masterType: null,
      item: null,
    });

    const [duplicateInactiveState, setDuplicateInactiveState] = useState({
      isOpen: false,
      masterType: null,
      existing: null,
    });

    const masterModalSubmitLockRef = useRef(false);

    // Modal states for each master
    const [modalStates, setModalStates] = useState({
      branch: {
        isOpen: false,
        editing: null,
        data: { name: "", address: "", status: "active" },
      },
      department: {
        isOpen: false,
        editing: null,
        data: { name: "", departmentHead: "", status: "active" },
      },
      designation: {
        isOpen: false,
        editing: null,
        data: {
          name: "",
          departmentId: null,
          jobDescription: {
            heading: "",
            description: "",
            experience: "",
            qualifications: "",
          },
          salaryRangeFrom: "",
          salaryRangeTo: "",
          status: "active",
        },
      },
      subDepartment: {
        isOpen: false,
        editing: null,
        data: { name: "", departmentId: "", status: "active" },
      },
      grade: {
        isOpen: false,
        editing: null,
        data: { name: "", status: "active" },
      },
      category: {
        isOpen: false,
        editing: null,
        data: { name: "", status: "active" },
      },
      reasons: {
        isOpen: false,
        editing: null,
        data: { name: "", type: "resignation", status: "active" },
      },
    });

    // Show deactivated items state
    const [showDeactivated, setShowDeactivated] = useState({
      branch: false,
      department: false,
      designation: false,
      subDepartment: false,
      grade: false,
      category: false,
      reasons: false,
    });

    // Actions dropdown: show on click instead of hover (key: `${tabName}-${item.id}` or null)
    const [openActionsKey, setOpenActionsKey] = useState(null);
    const openActionsRef = React.useRef(null);

    // Close actions dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (openActionsKey != null && openActionsRef.current && !openActionsRef.current.contains(e.target)) {
          setOpenActionsKey(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openActionsKey]);

    const handleSearchChange = (masterType, value) => {
      setSearchTerms((prev) => ({
        ...prev,
        [masterType]: value,
      }));
    };

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleLogoUpload = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () =>
          setFormData((prev) => ({ ...prev, logo: reader.result }));
        reader.readAsDataURL(file);
      }
    };

    const handleInviteAdminChange = (e) => setInviteAdmin(e.target.checked);

    const handleCreateCompanySubmit = () => {
      const newCompany = { id: companies.length + 1, ...formData };
      setCompanies((prev) => [...prev, newCompany]);
      handleCreateCompanyClose();
    };

    const handleSaveNew = () => {
      const newCompany = { id: companies.length + 1, ...formData };
      setCompanies((prev) => [...prev, newCompany]);
      handleReset();
    };

    const handleReset = () => {
      setFormData({
        code: "",
        name: "",
        address: "",
        email: "",
        phone: "",
        password: "",
        pfCode: "",
        esiCode: "",
        labourLicense: "",
        domainName: "",
        contactPerson: "",
        website: "",
        superAdminID: "",
        logo: null,
      });
      setInviteAdmin(false);
    };

    const handleCreateCompanyClose = () => {
      setIsCreateCompanyOpen(false);
      handleReset();
    };

    const handleUpdateCompany = () => {
      setIsEditCompanyOpen(false);
      handleReset();
    };

    // Master item actions
    const handleCreateItem = (masterType) => {
      setModalStates((prev) => ({
        ...prev,
        [masterType]: { ...prev[masterType], isOpen: true, editing: null },
      }));
    };

    const handleEditItem = (masterType, item) => {
      const defaultData = getDefaultData(masterType);
      let data = { ...item };
      if (masterType === "designation") {
        const jd = item.jobDescription;
        const jobDescription =
          jd && typeof jd === "object"
            ? {
                heading: jd.heading ?? "",
                description: jd.description ?? "",
                experience: jd.experience ?? "",
                qualifications: jd.qualifications ?? "",
              }
            : {
                heading: typeof jd === "string" ? jd : "",
                description: "",
                experience: item.experience ?? "",
                qualifications: item.qualifications ?? "",
              };
        data = {
          ...defaultData,
          ...item,
          jobDescription,
          salaryRangeFrom: item.salaryRangeFrom ?? item.salary_range_from ?? "",
          salaryRangeTo: item.salaryRangeTo ?? item.salary_range_to ?? "",
        };
      }
      setModalStates((prev) => ({
        ...prev,
        [masterType]: {
          ...prev[masterType],
          isOpen: true,
          editing: item,
          data,
        },
      }));
    };

    const performDeleteItem = async (masterType, item) => {
      let response;

      switch (masterType) {
        case "branch":
          response = await branchService.deleteBranch(item.id);
          break;
        case "department":
          response = await departmentService.deleteDepartment(item.id);
          break;
        case "designation":
          response = await designationService.deleteDesignation(item.id);
          break;
        case "subDepartment":
          response = await subDepartmentService.deleteSubDepartment(item.id);
          break;
        case "grade":
          response = await gradesService.deleteGrade(item.id);
          break;
        case "category":
          response = await categoryService.deleteCategory(item.id);
          break;
        case "reasons":
          response = await reasonsService.deleteReason(item.id);
          break;
        default:
          throw new Error(`Unknown master type: ${masterType}`);
      }

      if (!response.success) {
        throw new Error(response.message || "Delete failed");
      }
    };

    const handleDeleteItem = async (masterType, item) => {
      setConfirmState({ isOpen: true, masterType, item });
    };

    const handleConfirmDelete = async () => {
      const { masterType, item } = confirmState;
      if (!masterType || !item) {
        setConfirmState({ isOpen: false, masterType: null, item: null });
        return;
      }

      try {
        await performDeleteItem(masterType, item);
        toast.success(`${masterType} deleted successfully`);
        await fetchMasterData(masterType, showDeactivated[masterType]);
      } catch (error) {
        console.error(`Error deleting ${masterType}:`, error);
        toast.error(error.message || "Error deleting item");
      } finally {
        setConfirmState({ isOpen: false, masterType: null, item: null });
      }
    };

    const handleActivateDuplicate = async () => {
      const { masterType, existing } = duplicateInactiveState;
      if (!masterType || !existing?.id) {
        setDuplicateInactiveState({
          isOpen: false,
          masterType: null,
          existing: null,
        });
        return;
      }

      try {
        let response;
        const updateData = { ...existing, status: "active" };

        switch (masterType) {
          case "branch":
            response = await branchService.updateBranch(existing.id, updateData);
            break;
          case "department":
            response = await departmentService.updateDepartment(
              existing.id,
              updateData
            );
            break;
          case "designation":
            response = await designationService.updateDesignation(
              existing.id,
              updateData
            );
            break;
          case "subDepartment":
            response = await subDepartmentService.updateSubDepartment(
              existing.id,
              updateData
            );
            break;
          case "grade":
            response = await gradesService.updateGrade(existing.id, updateData);
            break;
          case "category":
            response = await categoryService.updateCategory(
              existing.id,
              updateData
            );
            break;
          case "reasons":
            response = await reasonsService.updateReason(existing.id, updateData);
            break;
          default:
            throw new Error(`Unknown master type: ${masterType}`);
        }

        if (!response?.success) {
          throw new Error(response?.message || "Activation failed");
        }

        toast.success(`${existing.name} activated successfully`);
        await fetchMasterData(masterType, showDeactivated[masterType]);

        setModalStates((prev) => ({
          ...prev,
          [masterType]: {
            isOpen: false,
            editing: null,
            data: getDefaultData(masterType),
          },
        }));
      } catch (error) {
        console.error(`Error activating ${masterType}:`, error);
        toast.error(error.message || "Error activating item");
      } finally {
        setDuplicateInactiveState({
          isOpen: false,
          masterType: null,
          existing: null,
        });
      }
    };

    const handleDeactivateItem = async (masterType, item) => {
      try {
        const updateData = { ...item, status: "inactive" };
        let response;

        switch (masterType) {
          case "branch":
            response = await branchService.updateBranch(item.id, updateData);
            break;
          case "department":
            response = await departmentService.updateDepartment(
              item.id,
              updateData
            );
            break;
          case "designation":
            response = await designationService.updateDesignation(
              item.id,
              updateData
            );
            break;
          case "subDepartment":
            response = await subDepartmentService.updateSubDepartment(
              item.id,
              updateData
            );
            break;
          case "grade":
            response = await gradesService.updateGrade(item.id, updateData);
            break;
          case "category":
            response = await categoryService.updateCategory(
              item.id,
              updateData
            );
            break;
          case "reasons":
            response = await reasonsService.updateReason(item.id, updateData);
            break;
          default:
            throw new Error(`Unknown master type: ${masterType}`);
        }

        if (response.success) {
          toast.success(`${item.name} deactivated successfully`);
          // Refresh data from backend - fetch all items if showDeactivated is true
          await fetchMasterData(masterType, showDeactivated[masterType]);
        } else {
          throw new Error(response.message || "Deactivation failed");
        }
      } catch (error) {
        console.error(`Error deactivating ${masterType}:`, error);
        toast.error(error.message || "Error deactivating item");
      }
    };

    const handleReactivateItem = async (masterType, item) => {
      try {
        const updateData = { ...item, status: "active" };
        let response;

        switch (masterType) {
          case "branch":
            response = await branchService.updateBranch(item.id, updateData);
            break;
          case "department":
            response = await departmentService.updateDepartment(
              item.id,
              updateData
            );
            break;
          case "designation":
            response = await designationService.updateDesignation(
              item.id,
              updateData
            );
            break;
          case "subDepartment":
            response = await subDepartmentService.updateSubDepartment(
              item.id,
              updateData
            );
            break;
          case "grade":
            response = await gradesService.updateGrade(item.id, updateData);
            break;
          case "category":
            response = await categoryService.updateCategory(
              item.id,
              updateData
            );
            break;
          case "reasons":
            response = await reasonsService.updateReason(item.id, updateData);
            break;
          default:
            throw new Error(`Unknown master type: ${masterType}`);
        }

        if (response.success) {
          toast.success(`${item.name} reactivated successfully`);
          // Refresh data from backend - fetch all items if showDeactivated is true
          await fetchMasterData(masterType, showDeactivated[masterType]);
        } else {
          throw new Error(response.message || "Reactivation failed");
        }
      } catch (error) {
        console.error(`Error reactivating ${masterType}:`, error);
        toast.error(error.message || "Error reactivating item");
      }
    };

    const handleModalSubmit = async (masterType) => {
      const modalState = modalStates[masterType];
      const { data, editing } = modalState;

      if (!data.name) {
        toast.error("Name is required");
        return;
      }

      if (masterModalSubmitLockRef.current) {
        toast.error("Please wait — save is already in progress.", {
          position: "top-right",
        });
        return;
      }
      masterModalSubmitLockRef.current = true;

      try {
        let response;

        if (editing) {
          // Update existing item
          switch (masterType) {
            case "branch":
              response = await branchService.updateBranch(editing.id, data);
              break;
            case "department":
              response = await departmentService.updateDepartment(
                editing.id,
                data
              );
              break;
            case "designation":
              response = await designationService.updateDesignation(
                editing.id,
                data
              );
              break;
            case "subDepartment":
              response = await subDepartmentService.updateSubDepartment(
                editing.id,
                data
              );
              break;
            case "grade":
              response = await gradesService.updateGrade(editing.id, data);
              break;
            case "category":
              response = await categoryService.updateCategory(editing.id, data);
              break;
            case "reasons":
              response = await reasonsService.updateReason(editing.id, data);
              break;
            default:
              throw new Error(`Unknown master type: ${masterType}`);
          }

          if (response.success) {
            toast.success(`${masterType} updated successfully`);
            // Refresh data from backend - fetch all items if showDeactivated is true
            await fetchMasterData(masterType, showDeactivated[masterType]);
          } else {
            throw new Error(response.message || "Update failed");
          }
        } else {
          // Create new item
          switch (masterType) {
            case "branch":
              response = await branchService.createBranch(data);
              break;
            case "department":
              response = await departmentService.createDepartment(data);
              break;
            case "designation":
              response = await designationService.createDesignation(data);
              break;
            case "subDepartment":
              response = await subDepartmentService.createSubDepartment(data);
              break;
            case "grade":
              response = await gradesService.createGrade(data);
              break;
            case "category":
              response = await categoryService.createCategory(data);
              break;
            case "reasons":
              response = await reasonsService.createReason(data);
              break;
            default:
              throw new Error(`Unknown master type: ${masterType}`);
          }

          if (response.success) {
            toast.success(`${masterType} created successfully`);
            // Refresh data from backend - fetch all items if showDeactivated is true
            await fetchMasterData(masterType, showDeactivated[masterType]);
          } else {
            throw new Error(response.message || "Creation failed");
          }
        }

        // Close modal and reset
        setModalStates((prev) => ({
          ...prev,
          [masterType]: {
            isOpen: false,
            editing: null,
            data: getDefaultData(masterType),
          },
        }));
      } catch (error) {
        const serverData = error?.response?.data;
        if (serverData?.code === "DUPLICATE_INACTIVE" && serverData?.existing) {
          setDuplicateInactiveState({
            isOpen: true,
            masterType,
            existing: serverData.existing,
          });
          return;
        }
        console.error(
          `Error ${editing ? "updating" : "creating"} ${masterType}:`,
          error
        );
        toast.error(
          error.message ||
            `Error ${editing ? "updating" : "creating"} ${masterType}`
        );
      } finally {
        masterModalSubmitLockRef.current = false;
      }
    };

    const getDefaultData = (masterType) => {
      switch (masterType) {
        case "branch":
          return { name: "", address: "", status: "active" };
        case "department":
          return { name: "", departmentHead: "", status: "active" };
        case "designation":
          return {
            name: "",
            departmentId: null,
            jobDescription: {
              heading: "",
              description: "",
              experience: "",
              qualifications: "",
            },
            salaryRangeFrom: "",
            salaryRangeTo: "",
            status: "active",
          };
        case "subDepartment":
          return { name: "", departmentId: "", status: "active" };
        case "grade":
          return { name: "", status: "active" };
        case "category":
          return { name: "", status: "active" };
        case "reasons":
          return { name: "", type: "resignation", status: "active" };
        default:
          return { name: "", status: "active" };
      }
    };

    const handleModalClose = (masterType) => {
      setModalStates((prev) => ({
        ...prev,
        [masterType]: {
          isOpen: false,
          editing: null,
          data: getDefaultData(masterType),
        },
      }));
    };

    const fetchMasterData = async (masterType, includeInactive = false) => {
      try {
        switch (masterType) {
          case "branch": {
            const branchResponse = await branchService.getBranches(
              includeInactive
            );
            if (branchResponse.success) {
              setBranches(branchResponse.data);
            }
            break;
          }
          case "department": {
            const deptResponse = await departmentService.getDepartments(
              includeInactive
            );
            if (deptResponse.success) {
              setDepartments(deptResponse.data);
            }
            break;
          }
          case "designation": {
            const designationResponse =
              await designationService.getDesignations(includeInactive);
            if (designationResponse.success) {
              setDesignations(designationResponse.data);
            }
            break;
          }
          case "subDepartment": {
            const subDeptResponse =
              await subDepartmentService.getAllSubDepartments(includeInactive);
            if (subDeptResponse.success) {
              setSubDepartments(subDeptResponse.data);
            }
            break;
          }
          case "grade": {
            const gradeResponse = await gradesService.getGrades(
              includeInactive
            );
            if (gradeResponse.success) {
              setGrades(gradeResponse.data);
            }
            break;
          }
          case "category": {
            const categoryResponse = await categoryService.getAllCategories(
              includeInactive
            );
            if (categoryResponse.success) {
              setCategories(categoryResponse.data);
            }
            break;
          }
          case "reasons": {
            const resignationResponse =
              await reasonsService.getAllResignationReasons(includeInactive);
            const terminationResponse =
              await reasonsService.getAllTerminationReasons(includeInactive);
            if (resignationResponse.success) {
              setResignationReasons(resignationResponse.data);
            }
            if (terminationResponse.success) {
              setTerminationReasons(terminationResponse.data);
            }
            break;
          }
          default:
            break;
        }
      } catch (error) {
        console.error(`Error fetching ${masterType} data:`, error);
      }
    };

    useEffect(() => {
      const fetchData = async (service, setter, errorMessage) => {
        try {
          setLoading(true);
          const response = await service();
          if (response.success) {
            setter(response.data);
          } else {
            toast.error(response.message || errorMessage);
          }
        } catch (error) {
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      };

      // Fetch employees for department head selection
      fetchData(
        employeeService.getEmployees,
        setEmployees,
        "Error fetching employees"
      );

      if (activeTab === "enterprise") {
        // Enterprise tab doesn't need specific data fetching
      } else if (activeTab === "masters") {
        // Fetch all master data
        fetchData(
          branchService.getBranches,
          setBranches,
          "Error fetching branches"
        );
        fetchData(
          departmentService.getDepartments,
          setDepartments,
          "Error fetching departments"
        );
        fetchData(
          designationService.getDesignations,
          setDesignations,
          "Error fetching designations"
        );
        fetchData(
          subDepartmentService.getAllSubDepartments,
          setSubDepartments,
          "Error fetching sub-departments"
        );
        fetchData(gradesService.getGrades, setGrades, "Error fetching grades");
        fetchData(
          categoryService.getAllCategories,
          setCategories,
          "Error fetching categories"
        );
        fetchData(
          reasonsService.getAllResignationReasons,
          setResignationReasons,
          "Error fetching resignation reasons"
        );
        fetchData(
          reasonsService.getAllTerminationReasons,
          setTerminationReasons,
          "Error fetching termination reasons"
        );
      }
    }, [activeTab]);

    // Refetch data when showDeactivated state changes
    useEffect(() => {
      if (activeTab === "masters") {
        masterTabs.forEach((tabName) => {
          if (showDeactivated[tabName]) {
            // Fetch all items (including inactive) when showDeactivated is true
            fetchMasterData(tabName, true);
          } else {
            // Fetch only active items when showDeactivated is false
            fetchMasterData(tabName, false);
          }
        });
      }
    }, [showDeactivated, activeTab]);

    const tabs = ["enterprise", "masters"];

    const masterTabs = [
      "branch",
      "department",
      "designation",
      "subDepartment",
      "grade",
      "category",
      "reasons",
    ];

    const renderMasterCard = (tabName) => {
      const cardData = {
        branch: {
          title: "Branch",
          description: "Manage company branches and locations",
          count: branches.length,
          data: branches,
          searchTerm: searchTerms.branch,
        },
        department: {
          title: "Department",
          description: "Organize departments and teams",
          count: departments.length,
          data: departments,
          searchTerm: searchTerms.department,
        },
        designation: {
          title: "Designation",
          description: "Define job roles and positions",
          count: designations.length,
          data: designations,
          searchTerm: searchTerms.designation,
        },
        subDepartment: {
          title: "Sub-Department",
          description: "Manage sub-departments within departments",
          count: subDepartments.length,
          data: subDepartments,
          searchTerm: searchTerms.subDepartment,
        },
        grade: {
          title: "Grade",
          description: "Define employee grades and levels",
          count: grades.length,
          data: grades,
          searchTerm: searchTerms.grade,
        },
        category: {
          title: "Category",
          description: "Categorize employees and positions",
          count: categories.length,
          data: categories,
          searchTerm: searchTerms.category,
        },
        reasons: {
          title: "Reasons",
          description: "Manage resignation and termination reasons",
          count: resignationReasons.length + terminationReasons.length,
          data: [
            ...resignationReasons.map((reason) => ({
              ...reason,
              type: reason.type || "resignation",
            })),
            ...terminationReasons.map((reason) => ({
              ...reason,
              type: reason.type || "termination",
            })),
          ],
          searchTerm: searchTerms.reasons,
        },
      };

      const data = cardData[tabName];
      const filteredData = data.data.filter((item) => {
        const matchesSearch = item.name
          .toLowerCase()
          .includes(data.searchTerm.toLowerCase());
        const matchesStatus = showDeactivated[tabName]
          ? true
          : item.status === "active" ||
            item.status === undefined ||
            item.status === null;
        return matchesSearch && matchesStatus;
      });

      return (
        <div
          key={tabName}
          className={`p-3 border transition-all duration-200 ${
            darkMode
              ? "bg-gray-800 text-gray-200 border-gray-700"
              : "bg-white text-gray-800 border-gray-200"
          }`}
          style={{ overflow: "visible", position: "relative", zIndex: 1 }}
        >
          {/* Card Header */}
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="text-sm font-semibold mb-0.5 text-red-600 dark:text-red-400">
                {data.title}
              </h3>
              <p className="text-xs text-gray-500">{data.description}</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() =>
                  setShowDeactivated((prev) => ({
                    ...prev,
                    [tabName]: !prev[tabName],
                  }))
                }
                className={`px-2 py-1 rounded text-xs ${
                  showDeactivated[tabName]
                    ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                {showDeactivated[tabName] ? "Hide Inactive" : "Show Inactive"}
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-2">
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={12}
              />
              <input
                type="text"
                value={data.searchTerm}
                onChange={(e) => handleSearchChange(tabName, e.target.value)}
                placeholder={`Search ${data.title.toLowerCase()}...`}
                className={`w-full pl-7 pr-2 py-1 border text-xs ${
                  darkMode
                    ? "bg-gray-700 text-gray-200 border-gray-600"
                    : "bg-gray-50 text-gray-800 border-gray-300"
                }`}
              />
            </div>
          </div>

          {/* Action Bar */}
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-gray-500">
              {filteredData.length} of {data.count} items
            </span>
            <button
              onClick={() => handleCreateItem(tabName)}
              className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                darkMode
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              <Plus size={12} className="inline mr-1" />
              Create
            </button>
          </div>

          {/* Table Content */}
          <div className="mt-2">
            {loading && data.data.length === 0 && (
              <div className="text-center py-2">
                <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
                  Loading...
                </p>
              </div>
            )}
            {!loading && filteredData.length === 0 && (
              <div className="text-center py-1 min-h-[168px] flex items-center justify-center">
                <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
                  {data.searchTerm
                    ? "No matching items found"
                    : `No ${data.title.toLowerCase()} found`}
                </p>
              </div>
            )}
            {!loading && filteredData.length > 0 && (
              <div className="master-card-table-container">
                <div className="master-card-scrollable min-h-[168px] max-h-[230px]">
                  <table className="w-full text-xs">
                    <thead
                      className={`sticky top-0 z-30 ${
                        darkMode ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <tr>
                        <th
                          className={`px-1 py-1 text-left text-xs font-medium tracking-wider sticky top-0 w-8 ${
                            darkMode ? "bg-gray-700" : "bg-gray-100"
                          }`}
                        >
                          Sl. No
                        </th>
                        <th
                          className={`px-2 py-1 text-left text-xs font-medium tracking-wider sticky top-0 ${
                            tabName === "reasons" ? "max-w-[80px]" : ""
                          } ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}
                        >
                          Name
                        </th>
                        {tabName === "reasons" && (
                          <th
                            className={`px-1 py-1 text-left text-xs font-medium tracking-wider sticky top-0 w-20 ${
                              darkMode ? "bg-gray-700" : "bg-gray-100"
                            }`}
                          >
                            Type
                          </th>
                        )}
                        <th
                          className={`px-1 py-1 text-left text-xs font-medium tracking-wider sticky top-0 w-16 ${
                            darkMode ? "bg-gray-700" : "bg-gray-100"
                          }`}
                        >
                          Status
                        </th>
                        <th
                          className={`px-1 py-1 text-left text-xs font-medium tracking-wider sticky top-0 w-10 ${
                            darkMode ? "bg-gray-700" : "bg-gray-100"
                          }`}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody
                      className={`${
                        darkMode ? "divide-gray-600" : "divide-gray-200"
                      } divide-y`}
                    >
                      {filteredData.map((item, index) => (
                        <tr
                          key={item.id || index}
                          className="hover:bg-gray-50 dark:hover:bg-gray-600 transition duration-150"
                        >
                          <td className="px-1 py-1 text-xs w-8">{index + 1}</td>
                          <td
                            className={`px-2 py-1 text-xs truncate ${
                              tabName === "reasons"
                                ? "max-w-[80px]"
                                : "max-w-[120px]"
                            }`}
                            title={item.name}
                          >
                            {item.name}
                          </td>
                          {tabName === "reasons" && (
                            <td className="px-1 py-1 text-xs w-20">
                              <span
                                className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap capitalize ${
                                  item.type === "resignation"
                                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                    : "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                }`}
                              >
                                {item.type || "resignation"}
                              </span>
                            </td>
                          )}
                          <td className="px-1 py-1 text-xs w-16">
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-xs whitespace-nowrap ${
                                item.status === "active" || !item.status
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                  : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                              }`}
                            >
                              {item.status === "active" || !item.status
                                ? "Active"
                                : "Inactive"}
                            </span>
                          </td>
                          <td
                            className="px-1 py-1 text-xs w-10"
                            style={{
                              position: "relative",
                              overflow: "visible",
                            }}
                          >
                            <div
                              ref={openActionsKey === `${tabName}-${item.id}` ? openActionsRef : null}
                              className="relative"
                              style={{ position: "relative" }}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenActionsKey((prev) =>
                                    prev === `${tabName}-${item.id}` ? null : `${tabName}-${item.id}`
                                  )
                                }
                                className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors relative z-10"
                              >
                                <MoreVertical size={10} />
                              </button>
                              <div
                                className={`action-dropdown-menu absolute right-0 top-full mt-1 w-28 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-2xl transition-all duration-200 ${
                                  openActionsKey === `${tabName}-${item.id}` ? "opacity-100 visible" : "opacity-0 invisible"
                                }`}
                                style={{ zIndex: 999999 }}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenActionsKey(null);
                                    handleEditItem(tabName, item);
                                  }}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                                >
                                  <Edit size={8} className="mr-1" />
                                  Edit
                                </button>
                                {item.status === "active" || !item.status ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenActionsKey(null);
                                      handleDeactivateItem(tabName, item);
                                    }}
                                    className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                                  >
                                    <Settings size={8} className="mr-1" />
                                    Deactivate
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenActionsKey(null);
                                      handleReactivateItem(tabName, item);
                                    }}
                                    className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-green-600"
                                  >
                                    <Settings size={8} className="mr-1" />
                                    Reactivate
                                  </button>
                                )}
                                {/* <button
                                  type="button"
                                  onClick={() => {
                                    setOpenActionsKey(null);
                                    handleDeleteItem(tabName, item);
                                  }}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-red-600"
                                >
                                  <Trash2 size={8} className="mr-1" />
                                  Delete
                                </button> */}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="flex-1">
        <style>{`
        .master-card-scrollable {
          overflow-x: hidden;
          overflow-y: auto;
          scrollbar-width: thin;
          position: relative;
        }
        .master-card-scrollable::-webkit-scrollbar {
          width: 6px;
        }
        .master-card-scrollable::-webkit-scrollbar-track {
          background: transparent;
        }
        .master-card-scrollable::-webkit-scrollbar-thumb {
          background-color: transparent;
          border-radius: 4px;
        }
        .master-card-scrollable:hover::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
        }
        .master-card-scrollable:hover::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.35);
        }
        .master-card-scrollable::-webkit-scrollbar-corner {
          background: transparent;
        }
        .dark .master-card-scrollable::-webkit-scrollbar-track {
          background: transparent;
        }
        .dark .master-card-scrollable:hover::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.2);
        }
        .dark .master-card-scrollable:hover::-webkit-scrollbar-thumb:hover {
          background-color: rgba(255, 255, 255, 0.35);
        }
        .dark .master-card-scrollable::-webkit-scrollbar-corner {
          background: transparent;
        }
        .master-card-scrollable {
          scrollbar-color: transparent transparent;
        }
        .master-card-scrollable:hover {
          scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
        }
        .dark .master-card-scrollable:hover {
          scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
        }
        .action-dropdown-menu {
          z-index: 999999 !important;
          position: absolute !important;
          pointer-events: auto !important;
        }
        .master-card-table-container {
          position: relative !important;
          overflow: visible !important;
          z-index: 1;
        }
        .master-card-table-container .master-card-scrollable {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          position: relative;
        }
        .master-card-table-container .master-card-scrollable table thead {
          position: sticky !important;
          top: 0 !important;
          z-index: 100 !important;
        }
        .master-card-table-container .master-card-scrollable table thead tr {
          position: relative;
        }
        .master-card-table-container .master-card-scrollable table thead th {
          position: sticky !important;
          top: 0 !important;
          z-index: 101 !important;
        }
        .master-card-table-container .master-card-scrollable table {
          position: relative;
        }
        .master-card-table-container .master-card-scrollable table tbody tr {
          position: relative;
        }
        .master-card-table-container .master-card-scrollable table tbody tr td:last-child {
          position: relative !important;
          overflow: visible !important;
          z-index: 10 !important; /* keep icon under sticky header */
        }
        .master-card-table-container .master-card-scrollable table tbody tr td:last-child .group {
          position: relative !important;
          z-index: 11 !important;
        }
        .master-card-table-container .master-card-scrollable table tbody tr td:last-child .action-dropdown-menu {
          position: absolute !important;
          z-index: 999999 !important;
          will-change: opacity, visibility;
          transform: translateZ(0);
          backface-visibility: hidden;
          isolation: isolate;
        }
      `}</style>
        <div
          className={`${
            darkMode
              ? "bg-gray-900 text-gray-200 dark"
              : "bg-white text-gray-800"
          } min-h-screen`}
        >
          {/* Fixed Header with Tabs */}
          <div
            className={`border-b sticky top-0 z-50 ${
              darkMode ? "bg-gray-900" : "bg-white"
            }`}
          >
            <div className="flex border-b border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`px-6 py-3 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? `${
                          darkMode
                            ? "bg-[#BB86FC] text-white border-b-2 border-[#BB86FC]"
                            : "bg-blue-600 text-white border-b-2 border-blue-600"
                        }`
                      : `${
                          darkMode
                            ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                            : "text-gray-600 hover:bg-gray-100"
                        }`
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "enterprise" ? "Enterprise" : "Masters"}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {activeTab === "enterprise" && <Enterprise darkMode={darkMode} />}

            {activeTab === "masters" && (
              <div>
                {/* Masters Cards - 4 per row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {masterTabs.map((tab) => renderMasterCard(tab))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals for each master type */}
        {masterTabs.map((masterType) => {
          const modalState = modalStates[masterType];
          if (!modalState.isOpen) return null;

          return (
            <Modal
              key={masterType}
              darkMode={darkMode}
              isOpen={modalState.isOpen}
              onClose={() => handleModalClose(masterType)}
              title={
                modalState.editing
                  ? `Edit ${
                      masterType.charAt(0).toUpperCase() + masterType.slice(1)
                    }`
                  : `Create ${
                      masterType.charAt(0).toUpperCase() + masterType.slice(1)
                    }`
              }
              onSubmit={() => handleModalSubmit(masterType)}
              submitText={modalState.editing ? "Update" : "Save"}
              onCancel={() => handleModalClose(masterType)}
            >
              <input
                type="text"
                value={modalState.data.name}
                onChange={(e) =>
                  setModalStates((prev) => ({
                    ...prev,
                    [masterType]: {
                      ...prev[masterType],
                      data: { ...prev[masterType].data, name: e.target.value },
                    },
                  }))
                }
                placeholder={`${
                  masterType.charAt(0).toUpperCase() + masterType.slice(1)
                } Name`}
                className={`w-full p-2 border rounded mb-4 ${
                  darkMode
                    ? "bg-gray-700 border-gray-600 text-gray-200"
                    : "bg-white border-gray-300 text-gray-800"
                }`}
              />
              {masterType === "branch" && (
                <input
                  type="text"
                  value={modalState.data.address || ""}
                  onChange={(e) =>
                    setModalStates((prev) => ({
                      ...prev,
                      [masterType]: {
                        ...prev[masterType],
                        data: {
                          ...prev[masterType].data,
                          address: e.target.value,
                        },
                      },
                    }))
                  }
                  placeholder="Address"
                  className={`w-full p-2 border rounded mb-4 ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-gray-200"
                      : "bg-white border-gray-300 text-gray-800"
                  }`}
                />
              )}
              {masterType === "department" && (
                <select
                  value={modalState.data.departmentHead || ""}
                  onChange={(e) =>
                    setModalStates((prev) => ({
                      ...prev,
                      [masterType]: {
                        ...prev[masterType],
                        data: {
                          ...prev[masterType].data,
                          departmentHead: e.target.value,
                        },
                      },
                    }))
                  }
                  className={`w-full p-2 border rounded mb-4 ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-gray-200"
                      : "bg-white border-gray-300 text-gray-800"
                  }`}
                >
                  <option value="">Select Department Head</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.user.name} ({emp.empCode})
                    </option>
                  ))}
                </select>
              )}
              {masterType === "designation" && (
                <>
                  <select
                    value={modalState.data.departmentId || ""}
                    onChange={(e) =>
                      setModalStates((prev) => ({
                        ...prev,
                        [masterType]: {
                          ...prev[masterType],
                          data: {
                            ...prev[masterType].data,
                            departmentId: e.target.value,
                          },
                        },
                      }))
                    }
                    className={`w-full p-2 border rounded mb-4 ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-gray-200"
                        : "bg-white border-gray-300 text-gray-800"
                    }`}
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Salary Range From
                      </label>
                      <input
                        type="number"
                        value={modalState.data.salaryRangeFrom || ""}
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                salaryRangeFrom: e.target.value,
                              },
                            },
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-gray-200"
                            : "bg-white border-gray-300 text-gray-800"
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Salary Range To
                      </label>
                      <input
                        type="number"
                        value={modalState.data.salaryRangeTo || ""}
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                salaryRangeTo: e.target.value,
                              },
                            },
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-gray-200"
                            : "bg-white border-gray-300 text-gray-800"
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Job Description
                    </label>
                    <textarea
                      type="textarea"
                      value={modalState.data.jobDescription?.heading || ""}
                      rows={4}
                      draggable={true}
                      onChange={(e) =>
                        setModalStates((prev) => ({
                          ...prev,
                          [masterType]: {
                            ...prev[masterType],
                            data: {
                              ...prev[masterType].data,
                              jobDescription: {
                                ...prev[masterType].data.jobDescription,
                                heading: e.target.value,
                              },
                            },
                          },
                        }))
                      }
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-gray-200"
                          : "bg-white border-gray-300 text-gray-800"
                      }`}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Experience
                      </label>
                      <input
                        type="text"
                        value={modalState.data.jobDescription?.experience || ""}
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                jobDescription: {
                                  ...prev[masterType].data.jobDescription,
                                  experience: e.target.value,
                                },
                              },
                            },
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-gray-200"
                            : "bg-white border-gray-300 text-gray-800"
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Qualifications
                      </label>
                      <input
                        type="text"
                        value={
                          modalState.data.jobDescription?.qualifications || ""
                        }
                        onChange={(e) =>
                          setModalStates((prev) => ({
                            ...prev,
                            [masterType]: {
                              ...prev[masterType],
                              data: {
                                ...prev[masterType].data,
                                jobDescription: {
                                  ...prev[masterType].data.jobDescription,
                                  qualifications: e.target.value,
                                },
                              },
                            },
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-gray-200"
                            : "bg-white border-gray-300 text-gray-800"
                        }`}
                      />
                    </div>
                  </div>
                </>
              )}
              {masterType === "subDepartment" && (
                <>
                  <select
                    value={modalState.data.departmentId || ""}
                    onChange={(e) =>
                      setModalStates((prev) => ({
                        ...prev,
                        [masterType]: {
                          ...prev[masterType],
                          data: {
                            ...prev[masterType].data,
                            departmentId: e.target.value,
                          },
                        },
                      }))
                    }
                    className={`w-full p-2 border rounded mb-4 ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-gray-200"
                        : "bg-white border-gray-300 text-gray-800"
                    }`}
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {/* {masterType === "grade" && (
              <input
                type="text"
                value={modalState.data.name}
                onChange={(e) =>
                  setModalStates((prev) => ({
                    ...prev,
                    [masterType]: {
                      ...prev[masterType],
                      data: { ...prev[masterType].data, name: e.target.value },
                    },
                  }))
                }
                placeholder="Grade Name"
                className={`w-full p-2 border rounded mb-4 ${
                  darkMode
                    ? "bg-gray-700 border-gray-600 text-gray-200"
                    : "bg-white border-gray-300 text-gray-800"
                }`}
              />
            )}
            {masterType === "category" && (
              <input
                type="text"
                value={modalState.data.name}
                onChange={(e) =>
                  setModalStates((prev) => ({
                    ...prev,
                    [masterType]: {
                      ...prev[masterType],
                      data: { ...prev[masterType].data, name: e.target.value },
                    },
                  }))
                }
                placeholder="Category Name"
                className={`w-full p-2 border rounded mb-4 ${
                  darkMode
                    ? "bg-gray-700 border-gray-600 text-gray-200"
                    : "bg-white border-gray-300 text-gray-800"
                }`}
              />
            )} */}
              {masterType === "reasons" && (
                <select
                  value={modalState.data.type || "resignation"}
                  onChange={(e) =>
                    setModalStates((prev) => ({
                      ...prev,
                      [masterType]: {
                        ...prev[masterType],
                        data: {
                          ...prev[masterType].data,
                          type: e.target.value,
                        },
                      },
                    }))
                  }
                  className={`w-full p-2 border rounded mb-4 ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-gray-200"
                      : "bg-white border-gray-300 text-gray-800"
                  }`}
                >
                  <option value="resignation">Resignation</option>
                  <option value="termination">Termination</option>
                </select>
              )}
            </Modal>
          );
        })}

        {(isCreateCompanyOpen || isEditCompanyOpen) && (
          <CompanyModal
            darkMode={darkMode}
            isEdit={isEditCompanyOpen}
            formData={formData}
            inviteAdmin={inviteAdmin}
            handleInputChange={handleInputChange}
            handleLogoUpload={handleLogoUpload}
            handleInviteAdminChange={handleInviteAdminChange}
            handleSubmit={
              isEditCompanyOpen
                ? handleUpdateCompany
                : handleCreateCompanySubmit
            }
            handleSaveNew={handleSaveNew}
            handleReset={handleReset}
            handleClose={() =>
              isEditCompanyOpen
                ? setIsEditCompanyOpen(false)
                : handleCreateCompanyClose()
            }
          />
        )}

        <Modal
          darkMode={darkMode}
          isOpen={confirmState.isOpen}
          onClose={() =>
            setConfirmState({ isOpen: false, masterType: null, item: null })
          }
          title="Confirm Delete"
          onSubmit={handleConfirmDelete}
          submitText="Delete"
          onCancel={() =>
            setConfirmState({ isOpen: false, masterType: null, item: null })
          }
        >
          <p className={darkMode ? "text-gray-200" : "text-gray-700"}>
            {confirmState.item
              ? `Are you sure you want to delete ${confirmState.item.name}?`
              : "Are you sure you want to delete this item?"}
          </p>
        </Modal>

        <Modal
          darkMode={darkMode}
          isOpen={duplicateInactiveState.isOpen}
          onClose={() =>
            setDuplicateInactiveState({
              isOpen: false,
              masterType: null,
              existing: null,
            })
          }
          title="Already Exists"
          onSubmit={handleActivateDuplicate}
          submitText="Activate"
          onCancel={() =>
            setDuplicateInactiveState({
              isOpen: false,
              masterType: null,
              existing: null,
            })
          }
        >
          <p className={darkMode ? "text-gray-200" : "text-gray-700"}>
            {duplicateInactiveState.existing?.name
              ? `${duplicateInactiveState.existing.name} already exists but is inactive. Do you want to activate it?`
              : "This item already exists but is inactive. Do you want to activate it?"}
          </p>
        </Modal>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.darkMode === nextProps.darkMode;
  }
);

export default Masters;
